import '../../motion.css';
import './build.css';
import { createContext, useContext, type ReactNode } from 'react';
import { stagger, useEnterOnce, useReducedMotion } from '@/lib/motion';

/** Delays after the frame starts to settle (NOTES-v3 moment c): summary, next steps, Publish last. */
export const REVEAL_AFTER_MS = { summary: 500, next: 700, publish: 850 } as const;

const RevealPlaying = createContext(false);

/**
 * True inside a reveal that is playing now. Viewers use it to decide whether a KPI counts up
 * (`<CountUp play={useRevealPlaying()} …/>`): numbers count up once, at the reveal, and are
 * still afterwards and on every later visit.
 */
export function useRevealPlaying(): boolean {
  return useContext(RevealPlaying);
}

/**
 * The done moment. `revealKey` names the moment (the saved revision): it plays once, the
 * first time that revision is revealed, and never again on a tab switch or a revisit.
 *
 * One soft glow around the frame; the content settles into focus; then the summary (UI1's
 * Checked card goes here), next steps and Publish arrive in that order. Then nothing moves.
 * The reveal animates what it is given; it never creates or implies a check receipt.
 */
export function BuildReveal({
  revealKey,
  children,
  summary,
  next,
  publish,
}: {
  revealKey: string;
  children: ReactNode;
  summary?: ReactNode;
  next?: ReactNode;
  publish?: ReactNode;
}) {
  const first = useEnterOnce(`reveal:${revealKey}`);
  const reduce = useReducedMotion();
  const playing = first && !reduce;
  const after = (delay: number) => (playing ? { className: 'pc-m-fade', style: stagger(0, { base: delay }) } : {});
  return (
    <RevealPlaying.Provider value={playing}>
      <div className="pc-reveal" data-testid="build-reveal" data-playing={playing ? 'true' : undefined}>
        <div className={playing ? 'pc-reveal-frame pc-m-glow' : 'pc-reveal-frame'}>
          <div className={playing ? 'pc-m-lens' : undefined}>{children}</div>
        </div>
        {(summary || next || publish) && (
          <div className="pc-reveal-after">
            {summary && (
              <div data-slot="summary" {...after(REVEAL_AFTER_MS.summary)}>
                {summary}
              </div>
            )}
            {next && (
              <div data-slot="next" {...after(REVEAL_AFTER_MS.next)}>
                {next}
              </div>
            )}
            {publish && (
              <div data-slot="publish" {...after(REVEAL_AFTER_MS.publish)}>
                {publish}
              </div>
            )}
          </div>
        )}
      </div>
    </RevealPlaying.Provider>
  );
}
